import type { DecisionAssessment } from '../decision/types';

export type SignalDataMode = 'LIVE' | 'DEMO' | 'CONFIGURATION_REQUIRED' | 'ERROR';
export type SignalKind = 'HOUSING' | 'SUPPORT' | 'SITE';
export type UpstreamCacheStatus = 'HIT' | 'MISS' | 'STALE' | 'BYPASS';

export interface UpstreamCacheMeta {
  status: UpstreamCacheStatus;
  ttlSeconds: number;
  staleTtlSeconds: number;
  upstreamFetchedAt?: string;
}

export type HousingComparisonAreaBasis = 'OFFICIAL_EXCLUSIVE' | 'HOUSE_TYPE_PROXY';
export type HousingMarketComparisonScope = 'SAME_UMD_SIMILAR_AREA' | 'DISTRICT_SIMILAR_AREA' | 'SAME_UMD_ALL_AREA' | 'DISTRICT_ALL_AREA';

export interface HousingUnitType {
  modelNo?: string;
  houseType: string;
  supplyAreaSqm?: number;
  comparisonAreaSqm?: number;
  comparisonAreaBasis?: HousingComparisonAreaBasis;
  generalSupplyUnits?: number;
  specialSupplyUnits?: number;
  totalSupplyUnits?: number;
  priceMax?: number;
  competitionRate?: number;
  applicants?: number;
}

export interface BusinessSignal {
  id: string;
  kind: SignalKind;
  title: string;
  summary?: string;
  category?: string;
  organization?: string;
  region?: string;
  address?: string;
  target?: string;
  amount?: number;
  amountProvenance?: 'OFFICIAL' | 'DERIVED';
  amountDetail?: string;
  units?: number;
  areaSqm?: number;
  publishedAt?: string;
  startsAt?: string;
  closesAt?: string;
  winnerAt?: string;
  sourceUrl?: string;
  sourceLabel: string;
  decision?: DecisionAssessment;

  // Optional decision-enrichment fields. They stay absent until an approved
  // official provider returns them; the Decision Engine never fabricates them.
  priceMin?: number;
  priceMax?: number;
  competitionRate?: number;
  specialSupplyCompetitionRate?: number;
  nearbyMedianSalePrice?: number;
  nearbyMedianJeonsePrice?: number;
  nearbyMedianUnitPricePerSqm?: number;
  marketReferenceEstimatedSalePrice?: number;
  marketReferenceHouseType?: string;
  marketReferenceAreaSqm?: number;
  marketReferenceAreaBasis?: HousingComparisonAreaBasis;
  marketSampleCount?: number;
  marketComparisonScope?: HousingMarketComparisonScope;
  marketComparisonDetail?: string;
  applicationMethod?: string;
  requiredDocumentCount?: number;
  selfContributionRate?: number;

  // Domain-level evidence kept from official provider responses instead of
  // collapsing every record into one generic summary.
  housingUnitTypes?: readonly HousingUnitType[];
  executingOrganization?: string;
  contact?: string;
  applicationUrl?: string;
  attachmentUrl?: string;
  attachmentName?: string;
}

export interface SignalApiResponse {
  mode: SignalDataMode;
  source: string;
  fetchedAt: string;
  signals: BusinessSignal[];
  cache?: UpstreamCacheMeta;
  message?: string;
}

export interface SignalFilters {
  keyword: string;
  region: string;
  category: string;
  closingWithinDays: number | null;
}

export const defaultSignalFilters: SignalFilters = {
  keyword: '',
  region: '',
  category: 'ALL',
  closingWithinDays: null,
};
